import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {get} from "lodash";
import React, {useState} from "react";
import {connect} from "react-redux";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";
import NavigationWarningModal from "components/Video/NavigationWarningModal";
import {useSelector} from 'react-redux';
import {color, font, type} from "common/tokens";
import Chip from "components/redesign/Chip";

const useStyles = makeStyles((theme) => ({
  ul: {
    listStyleType: 'none',
    display: 'flex',
    flexWrap: 'nowrap',
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 24,
    margin: 0,
    padding: 0,
    height: '100%',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 2,
      height: 'auto',
      // Padding sits on the rows rather than the list, so each one is a full
      // width target rather than text with a margin beside it.
      padding: 8,
      minWidth: 180,
    },
  },
  li: {
    display: 'flex',
    alignItems: 'stretch',
  },
  navText: {
    ...type.body,
    fontWeight: font.MEDIUM,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: color.TEXT_MUTED,
    textDecoration: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    borderBottom: '2px solid transparent',
    [theme.breakpoints.down('md')]: {
      // Fills the row, so the tap target and the fill are the whole width
      // rather than just the words.
      flex: 1,
      padding: '12px 16px',
      borderBottom: 'none',
      borderRadius: 10,
      justifyContent: 'space-between',
      '&:hover': {
        backgroundColor: color.SURFACE_MUTED,
      },
    },
  },
  current: {
    color: color.TEXT,
    fontWeight: font.SEMIBOLD,
    borderBottomColor: color.PURPLE,
    // The underline the desktop row uses has nowhere to sit in a stacked
    // list, so the current row is marked by its fill instead.
    [theme.breakpoints.down('md')]: {
      backgroundColor: color.ACCENT_TINT,
    },
  },
}));

const SignedInLinks = ({
  currentPath,
  experiences,
  handleClose,
  totalUnread,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const callInProgress = useSelector(state => state.video.callInProgress);

  const [showWarning, setShowWarning] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);

  const internship = get(experiences, "internship");
  const learninghub = get(experiences, "session");
  const mentorship = get(experiences, "curriculum");
  const survey = get(experiences, "survey");
  const chat = get(experiences, "chattype");
  const chatStatus = get(experiences, "chatStatus");
  const mentorshipChatStatus = get(chatStatus, "mentorshipChat");
  const aiChatStatus = get(chatStatus, "aiChat");

  // Called when user clicks any link
  const handleNavClick = (path) => {
    if (currentPath !== path) {
      setPendingPath(path);
      if(callInProgress) {
        setShowWarning(true);
      } else {
        history.push(path);
      }
    }
    if (handleClose) handleClose();
  };

  const handleContinue = () => {
    if (pendingPath) {
      localStorage.setItem('redirectAfterLogin', pendingPath);

      const fullUrl = `${window.location.origin}${pendingPath}`;
      window.open(fullUrl, "_blank", "noopener,noreferrer");
    }
    setShowWarning(false);
    setPendingPath(null);
  };

  const handleStay = () => {
    setShowWarning(false);
    setPendingPath(null);
  };

  const isVisible = (value) => value === "use" || value === "show";

  // Order follows the redesign: Mentorship first. Internships has no frame in the
  // design but stays visible for the programs that enable it.
  const links = [
    {key: "mentorship", show: isVisible(mentorship), path: "/mentorship", label: t("mentorship")},
    {key: "learninghub", show: isVisible(learninghub), path: "/learninghub", label: t("learningHub")},
    {key: "survey", show: isVisible(survey), path: "/surveylisting", label: t("survey")},
    {
      key: "chat",
      show: isVisible(chat) && (mentorshipChatStatus === "use" || aiChatStatus === "use"),
      path: "/chat",
      label: t("Chat"),
      badge: totalUnread > 0 ? totalUnread : null,
    },
    {key: "internship", show: isVisible(internship), path: "/internship", label: t("internships")},
  ];

  return (
    <>
      <ul className={classes.ul} onClick={handleClose}>
        {links.filter(link => link.show).map(link => (
          <li className={classes.li} key={link.key}>
            <div
              className={classNames(
                classes.navText,
                currentPath === link.key && classes.current
              )}
              onClick={() => handleNavClick(link.path)}
            >
              {link.label}
              {Boolean(link.badge) && <Chip variant={'count'}>{link.badge}</Chip>}
            </div>
          </li>
        ))}
      </ul>

      <NavigationWarningModal
        show={showWarning}
        onStay={handleStay}
        onContinue={handleContinue}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  experiences: get(state, "auth.user.status.experiences"),
});

export default connect(mapStateToProps)(SignedInLinks);
